// src/context/AuthContext.js — DR-Aura ULTIMATE v3.2
// FIXED: user init null · race condition · isPro safety · logout cleanup

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import {
  auth,
  onAuthChange,
  createUserDoc,
  getUserDoc,
  signInWithGoogle,
  signInEmail,
  signUpEmail,
  logOut,
  resetPassword,
  updateUserProfile
} from '../services/firebase';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);          // ✅ always start as null
  const [userDoc, setUserDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const refreshUserDoc = useCallback(async (uid) => {
    if (!uid) {
      if (mountedRef.current) setUserDoc(null);
      return;
    }
    try {
      const docData = await getUserDoc(uid);
      if (mountedRef.current) setUserDoc(docData);
    } catch (err) {
      console.warn('[AuthContext] refreshUserDoc:', err.message);
    }
  }, []);

  useEffect(() => {
    const unsub = onAuthChange(async (firebaseUser) => {
      try {
        if (!mountedRef.current) return;
        setUser(firebaseUser || null);   // ✅ ensure null when no user
        if (firebaseUser) {
          await createUserDoc(firebaseUser.uid ? firebaseUser.uid : firebaseUser); // safe create
          await refreshUserDoc(firebaseUser.uid);
        } else {
          if (mountedRef.current) setUserDoc(null);
        }
      } catch (err) {
        console.error('[AuthContext] auth change error:', err);
        if (mountedRef.current) setAuthError(err.message);
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    });
    return unsub;
  }, [refreshUserDoc]);

  // Robust isPro check
  const isPro = (() => {
    if (!userDoc || userDoc.plan !== 'pro' || !userDoc.subscriptionExpiry) return false;
    try {
      const expiry = userDoc.subscriptionExpiry?.toDate
        ? userDoc.subscriptionExpiry.toDate()
        : new Date(userDoc.subscriptionExpiry);
      return !isNaN(expiry.getTime()) && expiry > new Date();
    } catch {
      return false;
    }
  })();

  const googleLogin = async () => {
    setAuthError(null);
    try {
      const result = await signInWithGoogle();
      if (!result) return null;
      await createUserDoc(result.uid || result.user.uid);
      await refreshUserDoc(result.uid || result.user.uid);
      return result;
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user' && err.code !== 'auth/cancelled-popup-request') {
        throw err;
      }
    }
  };

  const emailLogin = async (email, password) => {
    setAuthError(null);
    const result = await signInEmail(email, password);
    if (!result) return null;
    await refreshUserDoc(result.uid || result.user.uid);
    return result;
  };

  const emailSignUp = async (email, password, displayName) => {
    setAuthError(null);
    const result = await signUpEmail(email, password, displayName);
    if (!result) return null;
    if (displayName) await updateUserProfile(result, { displayName });
    await createUserDoc(result.uid, { displayName: displayName || '' });
    await refreshUserDoc(result.uid);
    return result;
  };

  const logout = async () => {
    await logOut();
    if (mountedRef.current) {
      setUser(null);
      setUserDoc(null);
      setAuthError(null);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      userDoc,
      loading,
      isPro,
      authError,
      refreshUserDoc,
      googleLogin,
      emailLogin,
      emailSignUp,
      logout,
      resetPassword,
      // Convenience shortcuts
      savedCount: userDoc?.savedCount || 0,
      earnings: userDoc?.earnings || 0,
      plan: userDoc?.plan || 'free',
      displayName: user?.displayName || user?.email?.split('@')[0] || 'Chef',
    }}>
      {children}
    </AuthContext.Provider>
  );
};